import "./index.less";
import { AtTabBar, AtTabBarItem } from "../AtTabBar";
import { BaseResponsiveComponent, IBaseResponsiveComponentProps } from "../BaseResponsiveComponent";
import { CSSProperties } from "react";
import { HeightProperty } from "csstype";
import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { action, computed, observable } from "mobx";
import { inject, observer } from "mobx-react";
import React from "react";
import SidePanelItem, {
    ISidePanelItem,
    ISidePanelItemProps,
    SINGLE_LINE_SECTION_HEIGHT,
} from "./components/SidePanelItem";
import SidePanelSection from "./components/SidePanelSection";

export interface IPanelItemInfo {
    key: string;
    label: string;
    icon: IconProp;
    onClick?: () => void;
}

export interface IMainContentProps {
    content: React.ReactNode;
    headerContent?: React.ReactNode;
    customHeaderHeight?: HeightProperty<number>;
    contentClassName?: string;
    id?: string;
}

export interface IAtSidePanelLayoutProps extends IBaseResponsiveComponentProps {
    mainContentProps: IMainContentProps;
    sidePanels?: ISidePanelItem[];
    defaultActivePanelItemKey?: string;
    openDefaultSidePanelOnMobile?: boolean;
    scrollable?: boolean;
    className?: string;
}

@inject()
@observer
export class AtSidePanelLayout extends BaseResponsiveComponent<IAtSidePanelLayoutProps> {
    public mainContentContainerRef: React.RefObject<HTMLDivElement>;
    public mainContentViewRef: React.RefObject<HTMLDivElement>;
    public mainContentHeaderRef: React.RefObject<HTMLDivElement>;

    constructor(props) {
        super(props);

        this.mainContentContainerRef = React.createRef();
        this.mainContentViewRef = React.createRef();
        this.mainContentHeaderRef = React.createRef();
    }

    @observable protected activePanelItemKey?: string;

    @computed protected get rootElementClassName(): string | undefined {
        const { className } = this.props;

        return `AtSidePanelLayout ${className ?? ""}`;
    }

    componentDidMount(): void {
        const { openDefaultSidePanelOnMobile = true } = this.props;

        if (this.isDefault || (this.isMobile && openDefaultSidePanelOnMobile)) {
            this.activePanelItemKey = this.props.defaultActivePanelItemKey;
        }

        super.componentDidMount();
    }

    @computed protected get sidePanelItemInfos(): IPanelItemInfo[] | undefined {
        const { sidePanels } = this.props;

        if (!sidePanels) {
            return undefined;
        }

        return sidePanels.map((x) => ({
            key: x.panelKey,
            label: x.panelLabel,
            icon: x.panelIcon,
            onClick: x.onClick,
        }));
    }

    @computed protected get activePanelProps(): ISidePanelItemProps | undefined {
        const { sidePanels = [] } = this.props;

        if (!sidePanels) {
            return undefined;
        }

        const sidePanel = sidePanels.find((x) => x.panelKey === this.activePanelItemKey);

        if (!sidePanel) {
            return undefined;
        }

        return {
            ...sidePanel,
            setActivePanelItem: this.setActivePanelItem,
        };
    }

    @computed protected get showMainContent(): boolean {
        if (this.isDefault) {
            return true;
        }

        return this.isStringNullEmptyOrUndefined(this.activePanelItemKey);
    }

    @action protected setActivePanelItem = (panelItemKey: string) => {
        if (this.activePanelItemKey !== panelItemKey) {
            this.activePanelItemKey = panelItemKey;
        }
    };

    @action public hideSidePanelInMobile = () => {
        if (!this.isDefault) {
            this.activePanelItemKey = undefined;
        }
    };

    renderAll() {
        const { mainContentProps, scrollable = true } = this.props;

        let contentClassName = "content";
        const headerClassName = "header";

        if (mainContentProps.contentClassName) {
            contentClassName += ` ${mainContentProps.contentClassName}`;
        }

        if (!mainContentProps.headerContent) {
            contentClassName += " no-header";
        }

        if (scrollable) {
            contentClassName += " scrollable";
        }

        let contentStyle: CSSProperties = {
            overflowY: scrollable ? "auto" : "hidden",
        };
        let headerStyle: CSSProperties = {};

        if (mainContentProps.customHeaderHeight) {
            headerStyle = {
                height: mainContentProps.customHeaderHeight,
            };
            contentStyle = {
                top: mainContentProps.customHeaderHeight,
            };
        }

        const showPanelNavigator = this.sidePanelItemInfos && this.sidePanelItemInfos.length > 1; // We hide the selector if we have only one panel.

        const externalVerticalOffset = showPanelNavigator ? SINGLE_LINE_SECTION_HEIGHT : 0;

        return (
            <>
                {this.activePanelProps && (
                    <div className="active-sidebar-panel">
                        {showPanelNavigator && (
                            <SidePanelSection>
                                <AtTabBar>
                                    {this.sidePanelItemInfos?.map((x) => (
                                        <AtTabBarItem
                                            key={x.key}
                                            name={x.label}
                                            selected={this.activePanelItemKey === x.key}
                                            onClick={x.onClick ?? (() => this.setActivePanelItem(x.key))}
                                        />
                                    ))}
                                </AtTabBar>
                            </SidePanelSection>
                        )}

                        <SidePanelItem
                            hideTitle={true}
                            {...Object.assign({}, this.activePanelProps, { externalVerticalOffset })}
                        />
                    </div>
                )}

                {this.showMainContent && (
                    <div className="main-content" id={mainContentProps.id} ref={this.mainContentContainerRef}>
                        {mainContentProps.headerContent && (
                            <div className={headerClassName} style={headerStyle} ref={this.mainContentHeaderRef}>
                                {mainContentProps.headerContent}
                            </div>
                        )}
                        <div className={contentClassName} style={contentStyle} ref={this.mainContentViewRef}>
                            {mainContentProps.content}
                        </div>
                    </div>
                )}
            </>
        );
    }
}

export { SidePanelItem, ISidePanelItem };
